import { useState, useEffect } from 'react'
import {
    Box,
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Button
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '../../../config/firebase'
import { Sale } from '@starwave/shared/src/types'
import { useNavigate } from 'react-router-dom'
import { format } from 'date-fns'

export default function SalesPage() {
    const [sales, setSales] = useState<Sale[]>([])
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate()

    useEffect(() => {
        const q = query(collection(db, 'sales'), orderBy('date', 'desc'))
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const salesData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Sale[]
            setSales(salesData)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [])

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="h4" fontWeight="bold">
                    Sales History
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/admin/sales/new')}
                >
                    New Sale
                </Button>
            </Box>

            <Paper>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice #</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Customer</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Payment Mode</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sales.map((sale) => (
                                <TableRow key={sale.id}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{sale.invoiceNumber}</TableCell>
                                    <TableCell>
                                        {sale.date ? format(sale.date.toDate(), 'MMM dd, yyyy') : 'N/A'}
                                    </TableCell>
                                    <TableCell>{sale.customerName}</TableCell>
                                    <TableCell>PKR {sale.totalAmount.toLocaleString()}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={sale.paymentMode.replace('_', ' ').toUpperCase()}
                                            size="small"
                                            color={sale.paymentMode === 'credit' ? 'warning' : 'success'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {sale.outstandingAmount > 0 ? (
                                            <Chip label="Partial/Unpaid" color="error" size="small" />
                                        ) : (
                                            <Chip label="Paid" color="success" size="small" />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary">
                                            <VisibilityIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {sales.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">No sales recorded yet</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Container>
    )
}
